/**
 * Dashboard Page - CXO Command Centre
 * Main dashboard with Power BI embed and floating AI chatbot
 * Chatbot powered by Spera Digital's own backend (replaces AskYourDatabase)
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LogOut,
  Settings,
  MessageSquare,
  X,
  Building2,
  Minimize2,
  Maximize2,
  RefreshCw,
  Loader2,
  Send,
  Bot,
  History,
  Mic,
  MicOff
} from 'lucide-react';

// ─── Config ──────────────────────────────────────────────────────────────────
const CHATBOT_API     = process.env.REACT_APP_CHATBOT_API_URL || 'https://myai.speradigital.com';
const CHATBOT_API_KEY = process.env.REACT_APP_CHATBOT_API_KEY || 'sk-sp-9GmOdsmQCJm1WTJEY7dXrJFEPvERAlmksslyz5SHLM8';
const CHATBOT_ID      = process.env.REACT_APP_CHATBOT_ID     || '3d72aaf4-79ba-492f-8314-0edc83c1c36f';

const WELCOME_MESSAGE = {
  id: 'welcome',
  role: 'assistant',
  content: `Welcome to the **CXO Command Centre Intelligence Chatbot** 👋

I'm your AI-powered data analyst, connected directly to your SAP HANA payroll and HR database. You can ask me questions like:

• *What is the total payroll cost for May 2026?*
• *Show me attendance trends for Site A this quarter*
• *Compare government vs internal CTC for the Quality department*
• *List employees with attrition in the last 3 months*

How can I help you today?`,
  isWelcome: true,
};

// ─── Markdown renderer (lightweight, no external lib) ────────────────────────
function renderMarkdown(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/•\s/g, '• ')
    .replace(/\n/g, '<br/>');
}

// ─── Single message bubble ───────────────────────────────────────────────────
function ChatMessage({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: '12px',
      alignItems: 'flex-end',
      gap: '8px',
    }}>
      {!isUser && (
        <div style={msgStyles.avatar}>
          <Bot size={14} />
        </div>
      )}
      <div style={{
        ...msgStyles.bubble,
        ...(isUser ? msgStyles.userBubble : msgStyles.botBubble),
        maxWidth: '82%',
      }}>
        {msg.streaming && !msg.content ? (
          <span style={msgStyles.typing}>
            <span /><span /><span />
          </span>
        ) : (
          <span
            style={{ lineHeight: 1.55, fontSize: '0.875rem' }}
            dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
          />
        )}
        {msg.streaming && msg.content && (
          <span style={msgStyles.cursor}>▍</span>
        )}
      </div>
    </div>
  );
}

const msgStyles = {
  avatar: {
    width: '28px', height: '28px', borderRadius: '50%',
    background: 'linear-gradient(135deg, #06b6d4 0%, #7c3aed 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'white', flexShrink: 0,
  },
  bubble: {
    padding: '10px 14px',
    borderRadius: '16px',
    wordBreak: 'break-word',
  },
  userBubble: {
    background: 'linear-gradient(135deg, #06b6d4 0%, #7c3aed 100%)',
    color: 'white',
    borderBottomRightRadius: '4px',
  },
  botBubble: {
    background: '#11182d',
    color: '#cbd5e1',
    border: '1px solid rgba(148,163,184,0.18)',
    borderBottomLeftRadius: '4px',
  },
  typing: {
    display: 'inline-flex', gap: '4px', alignItems: 'center', height: '18px',
  },
  cursor: {
    display: 'inline-block',
    animation: 'blink 0.8s step-end infinite',
    marginLeft: '2px',
    color: '#22d3ee',
    fontWeight: 'bold',
  },
};

// ─── Main chatbot hook ────────────────────────────────────────────────────────
function useSperaChatbot() {
  const sessionRef = useRef(null);

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${CHATBOT_API_KEY}`,
  };

  const sendMessage = useCallback(async (message, onChunk, onDone, onError) => {
    try {
      if (!sessionRef.current) sessionRef.current = crypto.randomUUID();
      const res = await fetch(`${CHATBOT_API}/api/v1/embedded/chat`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          chatbot_id: CHATBOT_ID,
          message,
          session_id: sessionRef.current,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      sessionRef.current = data.session_id;
      if (data.response) onChunk(data.response);
      onDone(data);
    } catch (err) {
      onError(err.message);
    }
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const res = await fetch(
        `${CHATBOT_API}/api/v1/embedded/conversations/${CHATBOT_ID}`,
        { headers: authHeaders }
      );
      if (!res.ok) return [];
      return await res.json();
    } catch { return []; }
  }, []);

  const loadHistory = useCallback(async (session_id) => {
    try {
      const res = await fetch(
        `${CHATBOT_API}/api/v1/embedded/history/${session_id}?chatbot_id=${CHATBOT_ID}`,
        { headers: authHeaders }
      );
      if (!res.ok) return null;
      return await res.json();
    } catch { return null; }
  }, []);

  const resetConversation = useCallback(() => {
    sessionRef.current = null;
  }, []);

  const setSession = useCallback((id) => {
    sessionRef.current = id;
  }, []);

  return { sendMessage, resetConversation, loadConversations, loadHistory, setSession };
}

// ─── Voice input hook ────────────────────────────────────────────────────────
function useVoiceInput({ onInterimTranscript, onFinalTranscript, silenceTimeoutMs = 1500 } = {}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [voiceError, setVoiceError]   = useState(null);

  const recognitionRef     = useRef(null);
  const finalTranscriptRef = useRef('');
  const silenceTimerRef    = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setIsSupported(!!SR);
  }, []);

  const clearSilenceTimer = () => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  };

  const stopRecording = useCallback(() => {
    clearSilenceTimer();
    if (recognitionRef.current) recognitionRef.current.stop();
  }, []);

  const startRecording = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setIsSupported(false);
      setVoiceError("Voice input isn't supported in this browser. Please use Chrome or Edge, or type your question instead.");
      return;
    }
    setVoiceError(null);
    finalTranscriptRef.current = '';

    const recognition = new SR();
    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => setIsRecording(true);

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const piece = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscriptRef.current += piece + ' ';
        } else {
          interim += piece;
        }
      }
      if (onInterimTranscript) {
        onInterimTranscript((finalTranscriptRef.current + interim).trim());
      }
      clearSilenceTimer();
      silenceTimerRef.current = setTimeout(() => stopRecording(), silenceTimeoutMs);
    };

    recognition.onerror = (event) => {
      if (event.error === 'no-speech') return;
      if (event.error === 'not-allowed' || event.error === 'permission-denied') {
        setVoiceError('Microphone access was denied. Please allow microphone permission and try again.');
      } else {
        setVoiceError('Voice recognition error: ' + event.error);
      }
      stopRecording();
    };

    recognition.onend = () => {
      clearSilenceTimer();
      setIsRecording(false);
      const finalText = finalTranscriptRef.current.trim();
      if (finalText && onFinalTranscript) onFinalTranscript(finalText);
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [onFinalTranscript, onInterimTranscript, silenceTimeoutMs, stopRecording]);

  const toggleRecording = useCallback(() => {
    if (isRecording) stopRecording(); else startRecording();
  }, [isRecording, startRecording, stopRecording]);

  useEffect(() => {
    return () => {
      clearSilenceTimer();
      if (recognitionRef.current) {
        recognitionRef.current.onend = null;
        recognitionRef.current.stop();
      }
    };
  }, []);

  return { isRecording, isSupported, voiceError, toggleRecording };
}

// ─── Chat Panel ───────────────────────────────────────────────────────────────
function ChatPanel({ expanded, onToggleExpand, onClose }) {
  const { sendMessage, resetConversation, loadConversations, loadHistory, setSession } = useSperaChatbot();
  const [messages, setMessages]           = useState([WELCOME_MESSAGE]);
  const [input, setInput]                 = useState('');
  const [busy, setBusy]                   = useState(false);
  const [showHistory, setShowHistory]     = useState(false);
  const [conversations, setConversations] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!showHistory) return;
    setHistoryLoading(true);
    loadConversations().then(data => {
      setConversations(data || []);
      setHistoryLoading(false);
    });
  }, [showHistory, loadConversations]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setBusy(true);
    setShowHistory(false);
    const userMsg = { id: Date.now(),     role: 'user',      content: text };
    const botMsg  = { id: Date.now() + 1, role: 'assistant', content: '', streaming: true };
    setMessages(prev => [...prev, userMsg, botMsg]);
    await sendMessage(
      text,
      (chunk) => setMessages(prev =>
        prev.map(m => m.id === botMsg.id ? { ...m, content: m.content + chunk } : m)
      ),
      () => setMessages(prev =>
        prev.map(m => m.id === botMsg.id ? { ...m, streaming: false } : m)
      ),
      (errMsg) => setMessages(prev =>
        prev.map(m => m.id === botMsg.id
          ? { ...m, content: `⚠️ ${errMsg}`, streaming: false }
          : m
        )
      ),
    );
    setBusy(false);
  }, [input, busy, sendMessage]);

  const handleNewChat = () => {
    resetConversation();
    setMessages([WELCOME_MESSAGE]);
    setInput('');
    setBusy(false);
    setShowHistory(false);
  };

  const handleSelectConversation = async (conv) => {
    setHistoryLoading(true);
    const data = await loadHistory(conv.session_id);
    setHistoryLoading(false);
    if (!data) return;
    setSession(conv.session_id);
    const restored = data.messages.map((m, i) => ({ id: i, role: m.role, content: m.content }));
    setMessages(restored.length ? restored : [WELCOME_MESSAGE]);
    setShowHistory(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const { isRecording, isSupported: voiceSupported, voiceError, toggleRecording } = useVoiceInput({
    onInterimTranscript: (text) => setInput(text),
    onFinalTranscript: (finalText) => {
      setInput(finalText);
      (async () => {
        if (!finalText || busy) return;
        setInput('');
        setBusy(true);
        setShowHistory(false);
        const userMsg = { id: Date.now(),     role: 'user',      content: finalText };
        const botMsg  = { id: Date.now() + 1, role: 'assistant', content: '', streaming: true };
        setMessages(prev => [...prev, userMsg, botMsg]);
        await sendMessage(
          finalText,
          (chunk) => setMessages(prev =>
            prev.map(m => m.id === botMsg.id ? { ...m, content: m.content + chunk } : m)
          ),
          () => setMessages(prev =>
            prev.map(m => m.id === botMsg.id ? { ...m, streaming: false } : m)
          ),
          (errMsg) => setMessages(prev =>
            prev.map(m => m.id === botMsg.id
              ? { ...m, content: `⚠️ ${errMsg}`, streaming: false }
              : m
            )
          ),
        );
        setBusy(false);
      })();
    },
    silenceTimeoutMs: 1500,
  });

  const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) +
      ' · ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  return (
    <div style={{
      ...panelStyles.panel,
      width: expanded ? '50%' : '420px',
      height: expanded ? 'calc(100vh - 70px)' : '600px',
    }}>
      {/* Header */}
      <div style={panelStyles.header}>
        <div style={panelStyles.headerLeft}>
          <div style={panelStyles.chatIcon}><MessageSquare size={20} /></div>
          <div>
            <h3 style={panelStyles.title}>AI Assistant</h3>
            <span style={panelStyles.subtitle}>Ask questions about your data</span>
          </div>
        </div>
        <div style={panelStyles.headerActions}>
          <button
            style={{ ...panelStyles.iconBtn, background: showHistory ? 'rgba(34,211,238,0.25)' : 'rgba(255,255,255,0.1)' }}
            onClick={() => setShowHistory(h => !h)}
            title="Chat History"
          >
            <History size={18} />
          </button>
          <button style={panelStyles.iconBtn} onClick={handleNewChat} title="New Chat">
            <RefreshCw size={18} />
          </button>
          <button style={panelStyles.iconBtn} onClick={onToggleExpand} title={expanded ? 'Minimize' : 'Expand'}>
            {expanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
          <button style={panelStyles.iconBtn} onClick={onClose} title="Close">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* History Sidebar */}
      {showHistory && (
        <div style={panelStyles.historyPanel}>
          <div style={panelStyles.historyHeader}>
            <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#e2e8f0' }}>Past Conversations</span>
            <button style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
              onClick={() => setShowHistory(false)}><X size={16} /></button>
          </div>
          <div style={panelStyles.historyList}>
            {historyLoading ? (
              <div style={panelStyles.historyEmpty}>
                <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', color: '#22d3ee' }} />
              </div>
            ) : conversations.length === 0 ? (
              <div style={panelStyles.historyEmpty}>No past conversations yet.</div>
            ) : (
              conversations.map(conv => (
                <button
                  key={conv.session_id}
                  style={panelStyles.historyItem}
                  onClick={() => handleSelectConversation(conv)}
                >
                  <div style={panelStyles.historyTitle}>{conv.title || 'Untitled conversation'}</div>
                  <div style={panelStyles.historyTime}>{formatTime(conv.updated_at)}</div>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Messages */}
      <div style={panelStyles.messages}>
        {messages.map(msg => <ChatMessage key={msg.id} msg={msg} />)}
        <div ref={bottomRef} />
      </div>

      {/* Input row */}
      <div style={panelStyles.inputRow}>
        <textarea
          style={panelStyles.textarea}
          rows={1}
          placeholder={isRecording ? 'Listening...' : 'Ask a question about your data...'}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={busy}
        />
        <button
          style={{
            ...panelStyles.micBtn,
            ...(isRecording ? panelStyles.micBtnActive : {}),
            opacity: (!voiceSupported || busy) ? 0.4 : 1,
          }}
          onClick={toggleRecording}
          disabled={!voiceSupported || busy}
          title={!voiceSupported ? 'Voice input not supported in this browser' : (isRecording ? 'Stop recording' : 'Record a voice question')}
        >
          {isRecording ? <MicOff size={18} /> : <Mic size={18} />}
        </button>
        <button
          style={{ ...panelStyles.sendBtn, opacity: (!input.trim() || busy) ? 0.45 : 1 }}
          onClick={handleSend}
          disabled={!input.trim() || busy}
          title="Send"
        >
          {busy
            ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
            : <Send size={18} />}
        </button>
      </div>

      {voiceError && (
        <div style={panelStyles.voiceErrorBanner}>{voiceError}</div>
      )}

      {/* Chatbot panel footer */}
      <div style={panelStyles.footer}>
        Powered by{' '}
        <a href="https://www.speradigital.com/" target="_blank" rel="noreferrer" style={panelStyles.footerLink}>
          Spera Digital ®
        </a>
      </div>

      <style>{`
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes spin  { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
}

const panelStyles = {
  panel: {
    position: 'fixed', bottom: '80px', right: '24px',
    background: '#0b1022',
    borderRadius: '16px',
    boxShadow: '0 20px 55px rgba(0,0,0,0.55)',
    border: '1px solid rgba(34,211,238,0.22)',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden', zIndex: 1000,
    transition: 'all 0.3s ease',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '1rem 1.25rem',
    background: 'linear-gradient(135deg, #101936 0%, #34205f 100%)',
    color: 'white', flexShrink: 0,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  chatIcon: {
    width: '40px', height: '40px', borderRadius: '10px',
    background: 'rgba(255,255,255,0.15)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  title:    { fontSize: '1rem', fontWeight: '600', margin: 0 },
  subtitle: { fontSize: '0.75rem', opacity: 0.8 },
  headerActions: { display: 'flex', gap: '0.5rem' },
  iconBtn: {
    width: '32px', height: '32px', borderRadius: '6px',
    background: 'rgba(255,255,255,0.1)', border: 'none',
    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
  },
  messages: {
    flex: 1, overflowY: 'auto', padding: '1rem',
    background: '#070b18', display: 'flex', flexDirection: 'column',
  },
  inputRow: {
    display: 'flex', alignItems: 'flex-end', gap: '8px',
    padding: '10px 12px',
    borderTop: '1px solid rgba(34,211,238,0.14)',
    background: '#0b1022', flexShrink: 0,
  },
  textarea: {
    flex: 1, resize: 'none', background: '#11182d',
    border: '1px solid rgba(148,163,184,0.25)',
    borderRadius: '10px', color: '#e2e8f0',
    padding: '8px 12px', fontSize: '0.875rem',
    outline: 'none', maxHeight: '120px', lineHeight: 1.5,
    fontFamily: 'inherit',
  },
  sendBtn: {
    width: '38px', height: '38px', flexShrink: 0,
    borderRadius: '10px', border: 'none',
    background: 'linear-gradient(135deg, #06b6d4 0%, #7c3aed 100%)',
    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', transition: 'opacity 0.2s',
  },
  micBtn: {
    width: '38px', height: '38px', flexShrink: 0,
    borderRadius: '10px', border: '1px solid rgba(148,163,184,0.25)',
    background: '#11182d',
    color: '#a5b4fc', display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', transition: 'all 0.2s',
  },
  micBtnActive: {
    background: '#ef4444',
    borderColor: '#ef4444',
    color: 'white',
  },
  voiceErrorBanner: {
    fontSize: '0.7rem',
    color: '#fca5a5',
    background: 'rgba(239,68,68,0.10)',
    padding: '6px 12px',
    margin: '0 12px',
    borderRadius: '6px',
    textAlign: 'center',
  },
  footer: {
    padding: '8px 12px',
    borderTop: '1px solid rgba(34,211,238,0.10)',
    fontSize: '0.7rem', color: '#64748b',
    textAlign: 'center', background: '#0b1022', flexShrink: 0,
  },
  footerLink: {
    color: '#22d3ee', textDecoration: 'none', fontWeight: '600',
  },
  historyPanel: {
    position: 'absolute',
    top: '64px', left: 0, right: 0, bottom: '96px',
    background: '#0b1022', zIndex: 10,
    display: 'flex', flexDirection: 'column',
    borderBottom: '1px solid rgba(34,211,238,0.14)',
  },
  historyHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 14px',
    borderBottom: '1px solid rgba(148,163,184,0.15)',
    background: '#11182d', flexShrink: 0,
  },
  historyList:  { flex: 1, overflowY: 'auto', padding: '8px' },
  historyItem: {
    display: 'block', width: '100%', textAlign: 'left',
    padding: '10px 12px', marginBottom: '4px',
    background: '#11182d',
    border: '1px solid rgba(148,163,184,0.15)',
    borderRadius: '8px', cursor: 'pointer', transition: 'background 0.15s',
  },
  historyTitle: {
    fontSize: '0.8125rem', fontWeight: '500', color: '#cbd5e1',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    marginBottom: '3px',
  },
  historyTime:  { fontSize: '0.7rem', color: '#64748b' },
  historyEmpty: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100%', color: '#64748b', fontSize: '0.8125rem',
  },
};

// ─── Dashboard ────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [chatOpen, setChatOpen]         = useState(false);
  const [chatExpanded, setChatExpanded] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const powerBIUrl = "https://app.powerbi.com/reportEmbed?reportId=20764250-fab6-4079-aff5-283f9117caa8&autoAuth=true&ctid=99630fad-ad59-45ca-afa7-ca58c9c7d104";

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <Building2 size={28} style={{ color: '#22d3ee' }} />
          <div>
            <h1 style={styles.headerTitle}>CXO Command Centre</h1>
            <span style={styles.headerSubtitle}>Workforce Intelligence Platform</span>
          </div>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.userInfo}>
            <span style={styles.userName}>{user?.name || 'User'}</span>
            <span style={styles.userRole}>{user?.role === 'admin' ? 'Administrator' : 'User'}</span>
          </div>
          {isAdmin && (
            <button style={styles.headerButton} onClick={() => navigate('/settings')} title="Admin Settings">
              <Settings size={20} />
            </button>
          )}
          <button style={styles.logoutButton} onClick={handleLogout} title="Logout">
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </header>

      {/* Power BI embed */}
      <main style={styles.main}>
        <iframe
          title="CXO Command Centre"
          src={powerBIUrl}
          style={styles.powerBIFrame}
          frameBorder="0"
          allowFullScreen={true}
        />
      </main>

      {/* Floating chat button */}
      {!chatOpen && (
        <button style={styles.chatButton} onClick={() => setChatOpen(true)} title="Ask AI Assistant">
          <MessageSquare size={24} />
          <span style={styles.chatButtonLabel}>AI Assistant</span>
        </button>
      )}

      {/* Chat panel */}
      {chatOpen && (
        <ChatPanel
          expanded={chatExpanded}
          onToggleExpand={() => setChatExpanded(e => !e)}
          onClose={() => { setChatOpen(false); setChatExpanded(false); }}
        />
      )}

      {/* Page footer */}
      <footer style={styles.footer}>
        <span>© 2026 CXO Command Centre</span>
        <span style={styles.footerDivider}>|</span>
        <span>Powered by <strong style={{ color: '#22d3ee' }}>Spera Digital ®</strong></span>
      </footer>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#050816',
  },
  header: {
    height: '70px',
    background: 'linear-gradient(135deg, #070b18 0%, #111b3c 55%, #26164b 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 1.5rem',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.45)',
    borderBottom: '1px solid rgba(34, 211, 238, 0.22)',
    position: 'relative', zIndex: 100,
  },
  headerLeft:    { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  headerTitle: {
    fontFamily: "'Poppins', sans-serif", fontSize: '1.25rem',
    fontWeight: '700', color: 'white', margin: 0, lineHeight: 1.2,
  },
  headerSubtitle: { fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.8)' },
  headerRight:   { display: 'flex', alignItems: 'center', gap: '1rem' },
  userInfo:      { textAlign: 'right', marginRight: '0.5rem' },
  userName: {
    display: 'block', fontSize: '0.9375rem', fontWeight: '600', color: 'white',
  },
  userRole:      { fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.7)' },
  headerButton: {
    width: '40px', height: '40px', borderRadius: '8px',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', transition: 'all 0.2s ease',
  },
  logoutButton: {
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    padding: '0.5rem 1rem', borderRadius: '8px',
    background: 'rgba(244, 63, 94, 0.12)',
    border: '1px solid rgba(244, 63, 94, 0.35)',
    color: '#fb7185', fontSize: '0.875rem', fontWeight: '500',
    cursor: 'pointer', transition: 'all 0.2s ease',
  },
  main: { flex: 1, padding: '1rem', display: 'flex', flexDirection: 'column' },
  powerBIFrame: {
    flex: 1, width: '100%', border: 'none', borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(34, 211, 238, 0.12)',
    background: '#0b1022',
  },
  chatButton: {
    position: 'fixed', bottom: '80px', right: '24px',
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    padding: '0.875rem 1.25rem',
    background: 'linear-gradient(135deg, #06b6d4 0%, #7c3aed 100%)',
    color: 'white', border: 'none', borderRadius: '50px', cursor: 'pointer',
    boxShadow: '0 8px 28px rgba(34, 211, 238, 0.28)',
    transition: 'all 0.3s ease', zIndex: 1000,
    fontSize: '0.9375rem', fontWeight: '500',
  },
  chatButtonLabel: { display: 'inline' },
  footer: {
    height: '50px', background: '#070b18',
    borderTop: '1px solid rgba(34, 211, 238, 0.16)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '0.5rem', fontSize: '0.8125rem', color: '#64748b',
  },
  footerDivider: { color: '#cbd5e1' },
};

export default Dashboard;
